import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ApprovableData, TimeEntryData } from '@/types/api';

const KEY = ['time-entries'] as const;
const RUNNING_KEY = ['time-entries', 'running'] as const;
const UNBILLED_KEY = ['time-entries', 'unbilled-summary'] as const;

export type UnbilledSummaryRow = {
  client_id: number;
  client_name: string | null;
  entry_count: number;
  total_seconds: number;
  total_cents: number;
};

export function useTimeEntries(filters: {
  client_id?: number;
  project_id?: number;
  billable?: boolean;
  billed?: boolean;
  from?: string;
  to?: string;
  page?: number;
} = {}) {
  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: async (): Promise<Paginated<TimeEntryData>> => {
      const { data } = await api.get<Paginated<TimeEntryData>>('/v1/time-entries', { params: filters });
      return data;
    },
  });
}

export function useRunningTimer() {
  return useQuery({
    queryKey: RUNNING_KEY,
    queryFn: async (): Promise<TimeEntryData | null> => {
      const { data } = await api.get<TimeEntryData | null>('/v1/time-entries/running');
      return data;
    },
    refetchInterval: 30_000, // keep multi-tab views roughly in sync
    refetchOnWindowFocus: true,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      client_id: number;
      project_id?: number;
      card_id?: number;
      description?: string;
      billable?: boolean;
    }): Promise<TimeEntryData> => {
      await primeCsrf();
      const { data } = await api.post<TimeEntryData>('/v1/time-entries/start', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RUNNING_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<TimeEntryData | null> => {
      await primeCsrf();
      const { data } = await api.post<TimeEntryData | null>('/v1/time-entries/stop');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RUNNING_KEY });
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: UNBILLED_KEY });
    },
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      client_id: number;
      project_id?: number;
      card_id?: number;
      started_at: string;
      ended_at: string;
      description?: string;
      billable?: boolean;
      hourly_rate_cents?: number;
    }): Promise<TimeEntryData> => {
      await primeCsrf();
      const { data } = await api.post<TimeEntryData>('/v1/time-entries', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: UNBILLED_KEY });
    },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<{
        client_id: number;
        project_id: number | null;
        card_id: number | null;
        started_at: string;
        ended_at: string | null;
        description: string | null;
        billable: boolean;
        hourly_rate_cents: number | null;
      }>;
    }): Promise<TimeEntryData> => {
      await primeCsrf();
      const { data } = await api.patch<TimeEntryData>(`/v1/time-entries/${id}`, patch);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: UNBILLED_KEY });
    },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await primeCsrf();
      await api.delete(`/v1/time-entries/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: UNBILLED_KEY });
    },
  });
}

export function useDraftInvoiceFromTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      client_id: number;
      entry_ids?: number[];
      title?: string;
      recipient_email?: string;
      recipient_name?: string;
    }): Promise<ApprovableData> => {
      await primeCsrf();
      const { data } = await api.post<ApprovableData>('/v1/time-entries/draft-invoice', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: UNBILLED_KEY });
      qc.invalidateQueries({ queryKey: ['approvables'] });
    },
  });
}

export function useUnbilledSummary() {
  return useQuery({
    queryKey: UNBILLED_KEY,
    queryFn: async (): Promise<UnbilledSummaryRow[]> => {
      const { data } = await api.get<UnbilledSummaryRow[]>('/v1/time-entries/unbilled-summary');
      return data;
    },
  });
}

/**
 * Hours/minutes/seconds formatter for a count of seconds.
 * 2:05:30 for >= 1h, 5:30 for < 1h.
 */
export function formatDuration(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const s = Math.abs(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${sign}${h}:${pad(m)}:${pad(sec)}`;
  return `${sign}${pad(m)}:${pad(sec)}`;
}
